import React, { useState, useRef, useEffect } from 'react';

import { ShoppingCart, Search, Menu, X, ChevronDown, LayoutGrid, Gift, Tag, Star, Zap, TrendingUp, ChefHat, Home, Shirt, Car, Package, PawPrint, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '@/assets/ziko-market-logo.webp';
import LanguageSwitcher from './LanguageSwitcher';
import { useProducts } from '@/hooks/useProducts';
import PredictiveSearch from '@/components/storefront/PredictiveSearch';

interface NavbarProps {
  cartCount: number;
  onCartOpen: () => void;
}

// ── Full 3-level category hierarchy ──────────────────────────────────────
export interface NavCategory { label: string; to: string; children?: NavCategory[]; }

const RAW_CATEGORY_TREE: NavCategory[] = [
  {
    label: 'Home', to: '/category/Home', children: [
      { label: 'Indoor', to: '/category/Indoor', children: [
        { label: 'Cleaning', to: '/category/Cleaning' }, { label: 'Drinkware', to: '/category/Drinkware' },
        { label: 'Decor', to: '/category/Decor' }, { label: 'Furniture', to: '/category/Furniture' },
        { label: 'Gadgets', to: '/category/Gadgets' }, { label: 'Storage', to: '/category/Storage' },
        { label: 'Textiles', to: '/category/Textiles' }, { label: 'Lighting', to: '/category/Lighting' },
      ]},
      { label: 'Appliances', to: '/category/Appliances', children: [
        { label: 'Heaters', to: '/category/Heaters' }, { label: 'Coolers', to: '/category/Coolers' },
        { label: 'Small Appliances', to: '/category/Small+Appliances' },
      ]},
      { label: 'Outdoor', to: '/category/Outdoor', children: [
        { label: 'Gardening', to: '/category/Gardening' }, { label: 'Lighting', to: '/category/Outdoor+Lighting' },
      ]},
      { label: 'Kitchen', to: '/category/Kitchen', children: [
        { label: 'Food Processing', to: '/category/Food+Processing' }, { label: 'Cookware', to: '/category/Cookware' },
        { label: 'Accessories', to: '/category/Kitchen+Accessories' },
      ]},
      { label: 'Industrial & Scientific', to: '/category/Industrial' },
      { label: 'Office & School Supplies', to: '/category/Office+School' },
    ],
  },
  {
    label: 'Electronics', to: '/category/Electronics', children: [
      { label: 'PC Accessories', to: '/category/PC+Accessories', children: [
        { label: 'Keyboards', to: '/category/Keyboards' }, { label: 'Holders', to: '/category/Holders' },
        { label: 'Mouse', to: '/category/Mouse' }, { label: 'Headphones', to: '/category/Headphones' },
        { label: 'Speakers', to: '/category/Speakers' },
      ]},
      { label: 'Wireless', to: '/category/Wireless', children: [
        { label: 'Tablets', to: '/category/Tablets' }, { label: 'Mobile Phones', to: '/category/Mobile+Phones' },
      ]},
      { label: 'Mobile Accessories', to: '/category/Mobile+Accessories', children: [
        { label: 'Airpods', to: '/category/Airpods' }, { label: 'Cables', to: '/category/Cables' },
        { label: 'Flashes & Lights', to: '/category/Flashes' }, { label: 'Smart Accessories', to: '/category/Smart+Accessories' },
        { label: 'Smartwatches', to: '/category/Smartwatches' }, { label: 'Earphones', to: '/category/Earphones' },
      ]},
      { label: 'Chargers', to: '/category/Chargers', children: [
        { label: 'Cables', to: '/category/Charger+Cables' }, { label: 'Adapters', to: '/category/Adapters' },
        { label: 'Wireless Chargers', to: '/category/Wireless+Chargers' }, { label: 'Power Banks', to: '/category/Power+Banks' },
      ]},
      { label: 'Tech & Gaming', to: '/category/Tech+Gaming', children: [
        { label: 'Software', to: '/category/Software' }, { label: 'Video Games', to: '/category/Video+Games' },
      ]},
      { label: 'Other Electronics', to: '/category/Other+Electronics' },
    ],
  },
  {
    label: 'Fashion, Jewelry & Accessories', to: '/category/Fashion', children: [
      { label: 'Men', to: '/category/Men', children: [
        { label: 'Home', to: '/category/Men+Home' }, { label: 'Street', to: '/category/Men+Street' },
        { label: 'Pool & Beach', to: '/category/Men+Pool' }, { label: 'Club', to: '/category/Men+Club' },
        { label: 'Bags', to: '/category/Men+Bags' }, { label: 'Perfume', to: '/category/Men+Perfume' },
        { label: 'Watches', to: '/category/Men+Watches' }, { label: 'Accessories', to: '/category/Men+Accessories' },
        { label: 'Shoes', to: '/category/Men+Shoes' }, { label: 'Wallets', to: '/category/Wallets' },
        { label: 'Belts', to: '/category/Belts' }, { label: 'Jewelry', to: '/category/Men+Jewelry' },
      ]},
      { label: 'Women', to: '/category/Women', children: [
        { label: 'Home', to: '/category/Women+Home' }, { label: 'Street', to: '/category/Women+Street' },
        { label: 'Pool & Beach', to: '/category/Women+Pool' }, { label: 'Club', to: '/category/Women+Club' },
        { label: 'Bags', to: '/category/Women+Bags' }, { label: 'Perfume', to: '/category/Women+Perfume' },
        { label: 'Watches', to: '/category/Women+Watches' }, { label: 'Jewelry', to: '/category/Women+Jewelry' },
        { label: 'Shoes', to: '/category/Women+Shoes' },
      ]},
      { label: 'Kids', to: '/category/Kids', children: [
        { label: 'Boys', to: '/category/Boys' }, { label: 'Girls', to: '/category/Girls' },
      ]},
      { label: 'Kids & Maternity', to: '/category/Kids+Maternity', children: [
        { label: 'Boys', to: '/category/Boys' }, { label: 'Girls', to: '/category/Girls' },
        { label: 'Maternity', to: '/category/Maternity' },
      ]},
    ],
  },
  {
    label: 'Leisure', to: '/category/Leisure', children: [
      { label: 'Sports & Outdoors', to: '/category/Sports', children: [
        { label: 'Sports Equipment', to: '/category/Sports+Equipment' }, { label: 'Sports Products', to: '/category/Sports+Products' },
      ]},
      { label: 'Camping', to: '/category/Camping' },
      { label: 'Toys & Games', to: '/category/Toys+Games', children: [
        { label: 'Toys', to: '/category/Toys' }, { label: 'Games', to: '/category/Games' },
      ]},
    ],
  },
  {
    label: 'Automotive', to: '/category/Automotive', children: [
      { label: 'Car Fresheners', to: '/category/Car+Fresheners' }, { label: 'Cleaners', to: '/category/Car+Cleaners' },
      { label: 'Car Holders', to: '/category/Car+Holders' }, { label: 'Car Accessories', to: '/category/Car+Accessories' },
      { label: 'Car Audio', to: '/category/Car+Audio' }, { label: 'Car Repairs', to: '/category/Car+Repairs' },
      { label: 'Power Tools', to: '/category/Power+Tools' }, { label: 'Measuring Tools', to: '/category/Measuring+Tools' },
      { label: 'Hand Tools', to: '/category/Hand+Tools' },
    ],
  },
  {
    label: 'Consumables', to: '/category/Consumables', children: [
      { label: 'Beauty, Health & Personal Care', to: '/category/Beauty', children: [
        { label: 'Beauty Accessories', to: '/category/Beauty+Accessories' }, { label: 'Skin Care', to: '/category/Skin+Care' },
        { label: 'Hair Care', to: '/category/Hair+Care' }, { label: 'Perfumes', to: '/category/Perfumes' },
        { label: 'Cosmetics', to: '/category/Cosmetics' },
      ]},
      { label: 'Food & Beverage', to: '/category/Food+Beverage', children: [
        { label: 'Coffee Beans', to: '/category/Coffee+Beans' }, { label: 'Food', to: '/category/Food' },
        { label: 'Beverage', to: '/category/Beverage' }, { label: 'Snacks', to: '/category/Snacks' },
      ]},
    ],
  },
  {
    label: 'Pets', to: '/category/Pets', children: [
      { label: 'Food', to: '/category/Pet+Food' }, { label: 'Medicals', to: '/category/Pet+Medicals' },
      { label: 'Accessories', to: '/category/Pet+Accessories' },
    ],
  },
];

// The hand-written `to` slugs above are inconsistent — some use "+" for
// spaces, some drop "&" entirely, a few are abbreviated and don't match the
// label at all (e.g. "Fashion, Jewelry & Accessories" → "/category/Fashion").
// None of that round-trips through decodeURIComponent() on the receiving
// end, so any category with spaces/punctuation in its name silently matched
// zero products. Every `to` is recomputed here from the live label using
// proper URL encoding, so the link a user clicks always exactly reverses
// back to the label products are actually filtered by.
function withEncodedPaths(nodes: NavCategory[]): NavCategory[] {
  return nodes.map(node => ({
    ...node,
    to: `/category/${encodeURIComponent(node.label)}`,
    children: node.children ? withEncodedPaths(node.children) : undefined,
  }));
}

export const CATEGORY_TREE: NavCategory[] = withEncodedPaths(RAW_CATEGORY_TREE);

// Returns `label` plus every nested category label beneath it in CATEGORY_TREE
// (searched at any depth — L1, L2, or L3). Used so a parent category page
// (e.g. "Electronics") correctly includes products filed under its leaf
// categories (e.g. "Airpods"), since products always store the single most
// specific category label an admin picked. Falls back to [label] when the
// label isn't found in the tree at all (e.g. a category typed in via bulk
// CSV import that doesn't match the curated tree) so callers always get a
// usable, non-empty match set for both current and future products.
export function getAllDescendants(tree: NavCategory[], label: string): string[] {
  const target = label.trim().toLowerCase();

  const findNode = (nodes: NavCategory[]): NavCategory | undefined => {
    for (const node of nodes) {
      if (node.label.toLowerCase() === target) return node;
      if (node.children) {
        const found = findNode(node.children);
        if (found) return found;
      }
    }
    return undefined;
  };

  const collectLabels = (node: NavCategory): string[] => [
    node.label,
    ...(node.children?.flatMap(collectLabels) ?? []),
  ];

  const node = findNode(tree);
  return node ? collectLabels(node) : [label];
}

// Returns the full ancestor chain (root → ... → matching node, inclusive)
// for a given category label, searched at any depth. Used to build
// breadcrumb trails — e.g. "Airpods" → [Electronics, Mobile Accessories,
// Airpods]. Returns [] if the label isn't found anywhere in the tree (e.g.
// a category typed in via bulk CSV import that doesn't match the curated
// tree), so callers should fall back to showing the raw category string.
export function getCategoryPath(tree: NavCategory[], label: string): NavCategory[] {
  const target = label.trim().toLowerCase();

  function search(nodes: NavCategory[], path: NavCategory[]): NavCategory[] | null {
    for (const node of nodes) {
      const nextPath = [...path, node];
      if (node.label.toLowerCase() === target) return nextPath;
      if (node.children) {
        const found = search(node.children, nextPath);
        if (found) return found;
      }
    }
    return null;
  }

  return search(tree, []) ?? [];
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'Home': <Home className="h-3.5 w-3.5" />,
  'Electronics': <Zap className="h-3.5 w-3.5" />,
  'Fashion, Jewelry & Accessories': <Shirt className="h-3.5 w-3.5" />,
  'Leisure': <Star className="h-3.5 w-3.5" />,
  'Automotive': <Car className="h-3.5 w-3.5" />,
  'Consumables': <Package className="h-3.5 w-3.5" />,
  'Pets': <PawPrint className="h-3.5 w-3.5" />,
};

const GROUP_ORDER = ['Browse', 'Categories', 'Collections', 'Special'];

function categoryIcon(label: string) {
  const icon = CATEGORY_ICONS[label];
  if (icon) return { icon, accent: 'text-primary' };
  return { icon: <Tag className="h-3.5 w-3.5" />, accent: 'text-muted-foreground' };
}


// ── MegaMenu Component ────────────────────────────────────────────────────
function MegaMenu({ filteredTree, onClose }: { filteredTree: NavCategory[]; onClose: () => void }) {
  const { t } = useTranslation();
  const [activeTop, setActiveTop] = React.useState<string | null>(null);
  const [activeMid, setActiveMid] = React.useState<string | null>(null);
  const topItem = filteredTree.find(c => c.label === activeTop);
  const midItem = topItem?.children?.find(c => c.label === activeMid);

  return (
    <div className="absolute top-full start-0 mt-1 flex shadow-2xl border border-border/60 rounded-xl overflow-hidden"
      style={{ background: 'hsl(220 20% 6% / 0.98)', backdropFilter: 'blur(32px)', minWidth: 220, maxHeight: '70vh', zIndex: 200 }}>

      {/* Col 1: Browse + Top categories + Collections */}
      <div className="w-52 overflow-y-auto border-e border-border/30 py-2 flex-shrink-0">
        <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50">{t('nav.browse')}</p>
        {([{ label: t('nav.allProducts'), to: '/products' }, { label: t('nav.bestSellers'), to: '/best-sellers' }]).map(item => (
          <Link key={item.to} to={item.to} onClick={onClose}
            className="flex items-center gap-2.5 px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
            {item.to === '/products' ? <LayoutGrid className="h-3.5 w-3.5 text-primary" /> : <Star className="h-3.5 w-3.5 text-amber-400" />}
            {item.label}
          </Link>
        ))}
        <div className="h-px bg-border/30 mx-3 my-1.5" />
        <p className="px-4 pt-1 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50">{t('nav.categories')}</p>
        {filteredTree.map(cat => {
          const { icon } = categoryIcon(cat.label);
          return (
            <button key={cat.label} onClick={() => { setActiveTop(cat.label === activeTop ? null : cat.label); setActiveMid(null); }}
              className={"w-full flex items-center justify-between gap-2 px-4 py-2 text-sm transition-colors text-start " + (activeTop === cat.label ? "bg-secondary/60 text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/40")}>
              <span className="flex items-center gap-2">{icon}{cat.label}</span>
              {cat.children && <ChevronDown className="h-3 w-3 -rotate-90 rtl:rotate-90 shrink-0" />}
            </button>
          );
        })}
        <div className="h-px bg-border/30 mx-3 my-1.5" />
        <p className="px-4 pt-1 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50">{t('nav.collections')}</p>
        {([
          { label: t('home.essentials'), to: '/collection/essentials', color: 'text-blue-400' },
          { label: t('home.trendingNow'), to: '/collection/trending', color: 'text-amber-400' },
          { label: t('home.giftReady'), to: '/collection/gift-ready', color: 'text-emerald-400' },
        ]).map(c => (
          <Link key={c.to} to={c.to} onClick={onClose}
            className={"flex items-center gap-2.5 px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"}>
            <span className={"text-xs " + c.color}>●</span>{c.label}
          </Link>
        ))}
        <div className="h-px bg-border/30 mx-3 my-1.5" />
        <Link to="/gift-cards" onClick={onClose}
          className="flex items-center gap-2.5 px-4 py-2 pb-3 text-sm text-primary font-semibold hover:bg-primary/10 transition-colors">
          <Gift className="h-3.5 w-3.5" /> {t('nav.zikoGiftCards')}
        </Link>
      </div>

      {/* Col 2: Sub-categories */}
      {topItem?.children && (
        <div className="w-48 overflow-y-auto border-e border-border/30 py-2 flex-shrink-0">
          <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50">{topItem.label}</p>
          <Link to={topItem.to} onClick={onClose} className="flex items-center gap-1.5 px-4 py-1.5 text-xs text-primary hover:bg-primary/10 transition-colors">
            {t('nav.viewAllOf', { label: topItem.label })} <ArrowRight className="h-3 w-3 rtl:rotate-180" />
          </Link>
          {topItem.children.map(sub => (
            <button key={sub.label} onClick={() => setActiveMid(sub.label === activeMid ? null : sub.label)}
              className={"w-full flex items-center justify-between gap-2 px-4 py-2 text-sm transition-colors text-start " + (activeMid === sub.label ? "bg-secondary/60 text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/40")}>
              <span>{sub.label}</span>
              {sub.children && <ChevronDown className="h-3 w-3 -rotate-90 rtl:rotate-90 shrink-0" />}
            </button>
          ))}
        </div>
      )}

      {/* Col 3: Sub-sub-categories */}
      {midItem?.children && (
        <div className="w-44 overflow-y-auto py-2 flex-shrink-0">
          <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50">{midItem.label}</p>
          <Link to={midItem.to} onClick={onClose} className="flex items-center gap-1.5 px-4 py-1.5 text-xs text-primary hover:bg-primary/10 transition-colors">
            {t('nav.viewAllOf', { label: midItem.label })} <ArrowRight className="h-3 w-3 rtl:rotate-180" />
          </Link>
          {midItem.children.map(sub2 => (
            <Link key={sub2.label} to={sub2.to} onClick={onClose}
              className="flex items-center px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/40 transition-colors">
              {sub2.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MobileMenu Component ──────────────────────────────────────────────────
// Full accordion: L1 categories expand to L2, L2 expand to L3.
// Collections + utility links live below the category tree.
function MobileMenu({ filteredTree, onClose }: { filteredTree: NavCategory[]; onClose: () => void }) {
  const [openL1, setOpenL1] = React.useState<string | null>(null);
  const [openL2, setOpenL2] = React.useState<string | null>(null);
  const { t } = useTranslation();

  const toggleL1 = (label: string) => {
    setOpenL1(prev => prev === label ? null : label);
    setOpenL2(null);
  };
  const toggleL2 = (label: string) => {
    setOpenL2(prev => prev === label ? null : label);
  };

  return (
    <div className="overflow-y-auto max-h-[80vh] pb-6">
      {/* Browse */}
      <div className="px-4 pt-4 pb-1">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50 font-bold mb-2">{t('nav.browse')}</p>
        <div className="space-y-0.5">
          <Link to="/products" onClick={onClose} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
            <LayoutGrid className="h-4 w-4 text-primary shrink-0" /> {t('nav.allProducts')}
          </Link>
          <Link to="/best-sellers" onClick={onClose} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
            <Star className="h-4 w-4 text-amber-400 shrink-0" /> {t('nav.bestSellers')}
          </Link>
        </div>
      </div>

      {/* Categories — accordion */}
      <div className="px-4 pt-3 pb-1">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50 font-bold mb-2">{t('nav.categories')}</p>
        <div className="space-y-0.5">
          {filteredTree.map(cat => {
            const { icon } = categoryIcon(cat.label);
            const isOpenL1 = openL1 === cat.label;
            return (
              <div key={cat.label}>
                {/* L1 row */}
                <div className="flex items-center">
                  <Link
                    to={cat.to}
                    onClick={onClose}
                    className="flex-1 flex items-center gap-3 px-3 py-2.5 rounded-s-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                  >
                    <span className="shrink-0">{icon}</span>
                    {cat.label}
                  </Link>
                  {cat.children && (
                    <button
                      onClick={() => toggleL1(cat.label)}
                      className="px-3 py-2.5 rounded-e-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                      aria-label={`Expand ${cat.label}`}
                    >
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpenL1 ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>

                {/* L2 rows */}
                {isOpenL1 && cat.children && (
                  <div className="ms-4 mt-0.5 border-s border-border/40 ps-2 space-y-0.5">
                    <Link to={cat.to} onClick={onClose} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-primary hover:bg-primary/10 transition-colors">
                      {t('nav.viewAllOf', { label: cat.label })} <ArrowRight className="h-3 w-3 rtl:rotate-180" />
                    </Link>
                    {cat.children.map(sub => {
                      const isOpenL2 = openL2 === sub.label;
                      return (
                        <div key={sub.label}>
                          <div className="flex items-center">
                            <Link
                              to={sub.to}
                              onClick={onClose}
                              className="flex-1 flex items-center gap-2 px-3 py-2 rounded-s-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/40 transition-colors"
                            >
                              {sub.label}
                            </Link>
                            {sub.children && (
                              <button
                                onClick={() => toggleL2(sub.label)}
                                className="px-3 py-2 rounded-e-lg text-muted-foreground hover:text-foreground hover:bg-secondary/40 transition-colors"
                                aria-label={`Expand ${sub.label}`}
                              >
                                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isOpenL2 ? 'rotate-180' : ''}`} />
                              </button>
                            )}
                          </div>

                          {/* L3 rows */}
                          {isOpenL2 && sub.children && (
                            <div className="ms-4 mt-0.5 border-s border-border/30 ps-2 space-y-0.5">
                              <Link to={sub.to} onClick={onClose} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-primary hover:bg-primary/10 transition-colors">
                                {t('nav.viewAllOf', { label: sub.label })} <ArrowRight className="h-3 w-3 rtl:rotate-180" />
                              </Link>
                              {sub.children.map(leaf => (
                                <Link
                                  key={leaf.label}
                                  to={leaf.to}
                                  onClick={onClose}
                                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-colors"
                                >
                                  {leaf.label}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Collections */}
      <div className="px-4 pt-3 pb-1">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50 font-bold mb-2">{t('nav.collections')}</p>
        <div className="space-y-0.5">
          {([
            { label: t('home.essentials'),   to: '/collection/essentials',  emoji: '⚡', color: 'text-blue-400' },
            { label: t('home.trendingNow'), to: '/collection/trending',     emoji: '🔥', color: 'text-amber-400' },
            { label: t('home.giftReady'),   to: '/collection/gift-ready',   emoji: '🎁', color: 'text-emerald-400' },
          ]).map(c => (
            <Link key={c.to} to={c.to} onClick={onClose}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
              <span className="text-base">{c.emoji}</span>
              <span className={c.color + ' font-medium'}>{c.label}</span>
            </Link>
          ))}
          <Link to="/gift-cards" onClick={onClose}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-primary font-semibold hover:bg-primary/10 transition-colors">
            <Gift className="h-4 w-4 shrink-0" /> {t('nav.zikoGiftCards')}
          </Link>
        </div>
      </div>

      {/* Utility */}
      <div className="px-4 pt-3">
        <div className="h-px bg-border/30 mb-3" />
        <div className="space-y-0.5">
          <Link to="/track-order" onClick={onClose} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
            {t('nav.trackOrder')}
          </Link>
          <Link to="/policies" onClick={onClose} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
            {t('nav.policies')}
          </Link>
          <Link to="/contact" onClick={onClose} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
            {t('nav.contactUs')}
          </Link>
        </div>
      </div>
    </div>
  );
}

const Navbar = ({ cartCount, onCartOpen }: NavbarProps) => {
  const { t } = useTranslation();
  const { data: allProducts = [] } = useProducts();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const searchResults = searchQuery.trim().length > 1
    ? allProducts.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.description || '').toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 6)
    : [];

  useEffect(() => {
    if (searchOpen) setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [searchOpen]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const dynamicCategories = Array.from(new Set(allProducts.map(p => p.category))).filter(Boolean);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Filter CATEGORY_TREE to only show categories that have products
  const activeCategories = new Set(allProducts.map(p => p.category).filter(Boolean));

  const filteredTree = CATEGORY_TREE.filter(cat =>
    activeCategories.has(cat.label) ||
    cat.children?.some(sub =>
      activeCategories.has(sub.label) ||
      sub.children?.some(sub2 => activeCategories.has(sub2.label))
    )
  );

  // Keep dropdownChildren for flat mobile use
  const dropdownChildren = [
    { label: 'All Products', to: '/products' },
    { label: 'Best Sellers', to: '/best-sellers' },
  ];

  const navItems = [
    { label: t('bestSellers.title'), to: '/best-sellers' },
    {
      label: t('catalogue.allCategories'),
      to: '/products',
      children: dropdownChildren,
    },
    { label: 'Track Order', to: '/track-order' },
  ];

  return (
    <nav ref={navRef} className="fixed top-0 left-0 right-0 z-50 glass-strong">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" onClick={() => window.scrollTo({top:0,behavior:'smooth'})} className="flex items-center gap-3">
            <img src={logo} alt="ZKO Market" className="h-9 w-9 rounded-full" />
            <span className="font-display text-lg font-bold tracking-tight text-foreground">ZIKO</span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative group"
                onMouseEnter={() => item.children && setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Link
                  to={item.to}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-lg"
                >
                  {item.label}
                  {item.children && <ChevronDown className="h-3 w-3" />}
                </Link>

                {item.children && openDropdown === item.label && (
                  <MegaMenu
                    filteredTree={filteredTree}
                    onClose={() => setOpenDropdown(null)}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />

            {/* Search */}
            <div ref={searchRef} className="relative">
              {/* Search — icon button, opens full PredictiveSearch overlay */}
              <button onClick={() => setSearchOpen(true)} className="p-2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Search">
                <Search className="h-5 w-5" />
              </button>
              <AnimatePresence>
                {searchOpen && (
                  <>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="fixed inset-0 z-[100] bg-background/60 backdrop-blur-sm" onClick={() => setSearchOpen(false)} />
                    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.15 }}
                      className="fixed top-16 left-3 right-3 sm:absolute sm:top-full sm:mt-2 sm:left-auto sm:right-0 sm:w-[440px] z-[101]">
                      <PredictiveSearch onClose={() => setSearchOpen(false)} />
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
            <button onClick={onCartOpen} aria-label={`${t('cart.yourCart')}${cartCount > 0 ? ` (${cartCount})` : ''}`} className="relative p-2 text-muted-foreground hover:text-foreground transition-colors">
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <button aria-label={mobileOpen ? 'Close menu' : 'Open menu'} className="lg:hidden p-2 text-muted-foreground" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile menu ────────────────────────────────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden glass-strong border-t border-border overflow-hidden"
          >
            <MobileMenu filteredTree={filteredTree} onClose={() => setMobileOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;







